import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    BookOpen, 
    Loader2, 
    Crown,
    Lock,
    Sparkles
} from 'lucide-react';

export const DossiersPage = () => {
    const { user } = useAuth();
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDossiers();
    }, []);

    const loadDossiers = async () => {
        try {
            const response = await api.getDossiers();
            setDossiers(response.data);
        } catch (error) {
            console.error('Error loading dossiers:', error);
        } finally {
            setLoading(false);
        }
    };

    const isPremium = user?.subscription_tier === 'premium';

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 md:py-12 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-purple top-20 -right-40 opacity-20" />
            <div className="blob-orange bottom-40 -left-40 opacity-20" />
            
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
                        <Sparkles className="w-4 h-4" />
                        <span className="font-medium">Contenu Premium</span>
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-clip-text text-transparent">
                        Dossiers Spéciaux
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Explorez nos dossiers thématiques pour maîtriser des techniques, 
                        découvrir des cuisines du monde et économiser encore plus!
                    </p>
                </div>

                {/* Premium CTA for non-premium users */}
                {!isPremium && (
                    <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-3xl p-8 mb-12 text-white text-center">
                        <Crown className="w-12 h-12 mx-auto mb-4" />
                        <h2 className="font-heading text-2xl font-bold mb-2">
                            Accédez à tous les Dossiers Spéciaux
                        </h2>
                        <p className="text-white/80 mb-6 max-w-xl mx-auto">
                            Les Dossiers Spéciaux sont réservés aux membres Premium. 
                            Débloquez tout le contenu avec votre premier mois gratuit!
                        </p>
                        <Link to="/subscription">
                            <Button className="bg-white text-purple-600 hover:bg-white/90 font-bold px-8 py-6 rounded-full text-lg">
                                <Crown className="w-5 h-5 mr-2" />
                                Devenir Premium
                            </Button>
                        </Link>
                    </div>
                )}

                {/* Dossiers Grid */}
                {dossiers.length === 0 ? (
                    <div className="text-center py-20">
                        <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                        <h3 className="text-xl font-semibold text-muted-foreground">
                            Aucun dossier pour le moment
                        </h3>
                        <p className="text-muted-foreground">
                            De nouveaux dossiers arrivent bientôt!
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {dossiers.map((dossier) => (
                            <Link 
                                key={dossier.id} 
                                to={`/dossiers/${dossier.slug}`}
                                className="group"
                            >
                                <Card className="overflow-hidden rounded-2xl border-2 border-foreground/10 hover:border-purple-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                                    {/* Cover Image */}
                                    <div className="relative h-48 bg-gradient-to-br from-purple-100 to-pink-100">
                                        {dossier.cover_image ? (
                                            <img 
                                                src={dossier.cover_image} 
                                                alt={dossier.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <BookOpen className="w-16 h-16 text-purple-300" />
                                            </div>
                                        )}
                                        
                                        {/* Premium Badge */}
                                        <div className="absolute top-3 right-3">
                                            <Badge className="bg-gradient-to-r from-purple-600 to-pink-500 text-white border-0">
                                                <Crown className="w-3 h-3 mr-1" />
                                                Premium
                                            </Badge>
                                        </div>
                                        
                                        {/* Lock overlay for non-premium */}
                                        {!isPremium && (
                                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="bg-white/90 rounded-full p-3">
                                                    <Lock className="w-6 h-6 text-purple-600" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <CardContent className="p-5">
                                        <h3 className="font-heading text-xl font-bold mb-2 group-hover:text-purple-600 transition-colors">
                                            {dossier.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            {new Date(dossier.created_at).toLocaleDateString('fr-CA', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DossiersPage;
